import { SidekickHttpError } from '../sse/sidekickHttp';
import type { TransportRequestContext } from '../state/chatTypes';
import type { UseSidekickEventStreamResult } from './useSidekickEventStream.types';
import type { UseSidekickSyncResponseResult } from './useSidekickSyncResponse';

export const MAX_TRANSPORT_ATTEMPTS = 3;
const RETRY_AFTER_CEILING_MS = 60_000;

/** Message payload and request context for one transport attempt. */
export type ConversationTransportTurn = {
  content: string;
  conversationId: string;
} & TransportRequestContext;

/**
 * Converts a backend Retry-After value to a bounded delay.
 * @param retryAfterSeconds - Retry delay reported by the backend
 * @returns Delay in milliseconds, capped at one minute
 */
export const getRetryDelayMs = (retryAfterSeconds?: number): number => {
  if (
    retryAfterSeconds === undefined ||
    !Number.isFinite(retryAfterSeconds) ||
    retryAfterSeconds <= 0
  ) {
    return 0;
  }
  return Math.min(retryAfterSeconds * 1000, RETRY_AFTER_CEILING_MS);
};

/**
 * Schedules another transport attempt after the current cleanup completes.
 * @param callback - Retry callback
 * @param delayMs - Delay before retrying
 * @param onWaiting - Callback that restores the waiting UI
 * @returns Cleanup that cancels the scheduled retry
 */
export const scheduleAfterTransportCleanup = ({
  callback,
  delayMs,
  onWaiting,
}: {
  callback: () => void;
  delayMs: number;
  onWaiting: () => void;
}): (() => void) => {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;

  // A task boundary lets transport finalizers release the prior
  // controller before the waiting state is reaffirmed.
  timerId = setTimeout(() => {
    timerId = null;
    if (cancelled) {
      return;
    }
    onWaiting();
    if (delayMs > 0) {
      timerId = setTimeout(() => {
        timerId = null;
        if (!cancelled) {
          callback();
        }
      }, delayMs);
      return;
    }
    callback();
  });

  return () => {
    cancelled = true;
    if (timerId !== null) {
      clearTimeout(timerId);
    }
  };
};

type RunConversationTransportTurnParams = {
  turn: ConversationTransportTurn;
  isSseEnabled: boolean;
  eventStream: Pick<UseSidekickEventStreamResult, 'start'>;
  syncResponse: Pick<UseSidekickSyncResponseResult, 'send'>;
};

/**
 * Sends one conversation turn through the configured transport.
 * @param params - Current turn and available transport clients
 * @returns Promise resolved after the selected transport finishes sending
 */
export const runConversationTransportTurn = async ({
  turn,
  isSseEnabled,
  eventStream,
  syncResponse,
}: RunConversationTransportTurnParams): Promise<void> => {
  const input = {
    content: turn.content,
    conversationId: turn.conversationId,
    ...(turn.fleetId !== undefined ? { fleetId: turn.fleetId } : {}),
    ...(turn.interfaceName !== undefined
      ? { interface: turn.interfaceName }
      : {}),
  };
  if (isSseEnabled) {
    await eventStream.start(input);
    return;
  }

  await syncResponse.send(input).catch((error) => {
    if (import.meta.env.DEV) {
      console.warn(
        '[useConversation] unexpected sync send rejection',
        error instanceof SidekickHttpError
          ? error.sidekickError.code
          : (error?.constructor?.name ?? 'unknown')
      );
    }
  });
};
